import Fill from 'ol/style/Fill';
import GeoJSON from 'ol/format/GeoJSON';
import Stroke from 'ol/style/Stroke';
import Style from 'ol/style/Style';
import Text from 'ol/style/Text';

import {
  addFeaturesToLayer,
  clearMapLayer,
  hasLayerSource,
  createFeatureUsingGeojsonGeometry,
} from 'utils/map/layers';
import { getGeometryOfFeature } from 'utils/main';
import { ZONES_COLORS } from 'consts/mapConfig';

import { testDigits, testPhone, testText } from 'utils/form';

const getSelectedZoneStyle = zone => {
  const { name } = zone;
  const selectedZoneStyle = new Style({
    stroke: new Stroke({
      color: `rgba(${ZONES_COLORS.SELECTED},1)`,
      width: 3,
    }),
    fill: new Fill({
      color: `rgba(${ZONES_COLORS.SELECTED},0.4)`,
    }),
    text: new Text({
      text: `${name}`,
      stroke: new Stroke({
        color: 'BLACK',
        width: 1,
      }),
    }),
  });
  return selectedZoneStyle;
};

const getZoneStyle = zone => {
  const { type, name } = zone;
  return getStyles(type, name);
};

export const getStyles = (type, name) => {
  const polygonStyle = new Style({
    stroke: new Stroke({
      color: `rgba(${ZONES_COLORS[type]},0.5)`,
      width: 1,
    }),
    fill: new Fill({
      color: `rgba(${ZONES_COLORS[type]},0.25)`,
    }),
    text: new Text({
      text: `${name}`,
      stroke: new Stroke({
        color: 'BLACK',
        width: 1,
      }),
    }),
  });
  return polygonStyle;
};

const getInactiveZoneStyle = type =>
  new Style({
    stroke: new Stroke({
      color: `rgba(${ZONES_COLORS[type]},0.23)`,
      width: 1,
    }),
  });

export const addZonesFeaturesToLayer = (layer = null, zones = {}) => {
  if (!layer) return;
  clearMapLayer(layer);
  const parser = new GeoJSON();
  const features = [];
  for (const key of Object.keys(zones)) {
    if (zones[key]?.length) {
      for (const zone of zones[key]) {
        const geojson = getGeometryOfFeature(zone.geojson);
        if (geojson) {
          const feature = parser.readFeature(geojson, {
            dataProjection: 'EPSG:4326',
            featureProjection: 'EPSG:3857',
          });
          feature.setId(zone.uid);
          feature.setProperties({
            detail: 'zone',
            type: key,
            name: zone.name,
            uid: zone.uid,
          });
          feature.setStyle(getZoneStyle(zone));
          features.push(feature);
        }
      }
    }
  }
  addFeaturesToLayer(layer, features);
};

export const addPrivateZonesToLayer = (layer, zones = []) => {
  if (!layer) return;
  clearMapLayer(layer);
  const allFeatures = [];
  for (const zone of zones) {
    const parser = new GeoJSON();
    const features = parser.readFeatures(zone?.geojson, {
      dataProjection: 'EPSG:4326',
      featureProjection: 'EPSG:3857',
    });

    for (const feature of features) {
      feature.setId(zone.uid);
      feature.setProperties({
        detail: 'zone',
        name: zone.name,
        type: zone,
        uid: zone.uid,
      });
      allFeatures.push(feature);
    }
  }
  addFeaturesToLayer(layer, allFeatures);
};

export const addZonesToLayer = (layer, zones) => {
  const features = [];
  for (const zone of zones) {
    const feature = createFeatureUsingGeojsonGeometry(zone?.geojson);
    feature.setId(zone?.uid);
    feature.setProperties({ detail: 'zone', status: zone?.status });
    features.push(feature);
  }
  addFeaturesToLayer(layer, features);
};

export const setSelectedZoneStyle = selectedZones => {
  selectedZones[0].setStyle(
    getSelectedZoneStyle(selectedZones[0].getProperties())
  );
};

export const setBasicZonesStyles = layer => {
  if (!hasLayerSource(layer)) return;
  const zonesFeatures = layer.getSource().getFeatures();
  for (const feature of zonesFeatures) {
    feature.setStyle(getZoneStyle(feature.getProperties()));
  }
};

export const setInactiveZonesStyle = layer => {
  if (!hasLayerSource(layer)) return;
  const zonesFeatures = layer.getSource().getFeatures();
  for (const feature of zonesFeatures) {
    const { type } = feature.getProperties();
    feature.setStyle(getInactiveZoneStyle(type));
  }
};

export const getPrivateZonesFromAllZoneObjects = zoneList => {
  const localZones = {};
  if (!zoneList.length) return localZones;
  for (const zoneObject of zoneList) {
    if (zoneObject.category === 'zone') {
      const newType = `${zoneObject.type}_LOCAL`;
      if (localZones[newType]) localZones[newType].push(zoneObject);
      else localZones[newType] = [zoneObject];
    }
  }

  return localZones;
};

export const getCorridorsFromAllZoneObjects = zoneList => {
  const corridors = [];
  for (const zoneObject of zoneList) {
    if (zoneObject.category === 'corridor') {
      corridors.push(zoneObject);
    }
  }

  return corridors;
};

export const checkDataRanges = (array, newObject) => {
  const arrayOfObjectsThatCauseProblem = [];
  array.forEach((eachObject, index) => {
    if (
      (newObject.start > new Date(eachObject.start).getTime() &&
        newObject.start < new Date(eachObject.stop).getTime()) ||
      (newObject.stop > new Date(eachObject.start).getTime() &&
        newObject.stop < new Date(eachObject.stop).getTime()) ||
      (newObject.start < new Date(eachObject.start).getTime() &&
        newObject.stop > new Date(eachObject.stop).getTime())
    ) {
      arrayOfObjectsThatCauseProblem.push(index);
    }
  });

  return arrayOfObjectsThatCauseProblem;
};

export const getZonesByType = (allZones = {}, zonesTypes = []) => {
  const zonesData = {};
  for (const zoneType of zonesTypes) {
    zonesData[zoneType] = allZones[zoneType];
  }

  return zonesData;
};

export const getZonesByCategory = (allZones = {}, zonesCategories = []) => {
  const zonesData = {};
  for (const type of Object.keys(allZones)) {
    zonesData[type] = [];
    for (const zone of allZones[type]) {
      if (
        zone.zone_category &&
        zonesCategories.includes(zone.zone_category.uid)
      )
        zonesData[type].push(zone);
    }
  }

  return zonesData;
};

export const validateData = data => {
  const errors = {};
  if (!validateCheck('othername', data.othername)) errors.othername = true;
  if (!validateCheck('contact', data.contact)) errors.contact = true;
  if (!validateCheck('description', data.description))
    errors.description = true;
  if (!validateCheck('amsl_min', data.amsl_min)) errors.amsl_min = true;
  if (!validateCheck('amsl_max', data.amsl_max)) errors.amsl_max = true;
  if (!validateCheck('zone_category', data.zone_category))
    errors.zone_category = true;
  if (!validateCheck('geojson', data.geojson)) errors.geojson = true;
  if (!validateCheck('type', data.type)) errors.type = true;

  if (Object.keys(errors).length) return errors;
  return null;
};

export const validateCheck = (name, value) => {
  if (name === 'othername' && testText(value, 3)) return true;
  if (name === 'contact' && testPhone(value, 3, 12)) return true;
  if (name === 'description' && testText(value, 3)) return true;
  if (name === 'zone_category' && value && testText(value, 3)) return true;
  if (name === 'type' && testText(value, 3)) return true;
  if (
    name === 'amsl_min' &&
    testDigits(parseInt(value), 0, borderValues.maxHeight)
  )
    return true;
  if (
    name === 'amsl_max' &&
    testDigits(parseInt(value), 0, borderValues.maxHeight)
  )
    return true;

  if (name === 'geojson' && value) return true;

  return false;
};

export const borderValues = {
  minHeight: 0,
  maxHeight: 10000,
};
